import { Section, SectionType } from './sectionClassifier';
import { AnalysisResult } from '../lib/api';

/**
 * Group consecutive same-type sections so the snapshot can render
 *   ['Intro', 'Verse', 'Verse', 'Chorus', 'Drop', 'Outro']
 * as
 *   [{type:'intro', count:1}, {type:'verse', count:2}, ...]
 *
 * Bookend mix-in / mix-out are dropped — they're a DJ artifact, not part
 * of the artist-facing song shape.
 */
export interface StructureGroup {
  type: SectionType;
  count: number;
  startTime: number;
  endTime: number;
}

export function groupStructure(sections: Section[]): StructureGroup[] {
  const playable = sections.filter(s => s.type !== 'mix-in' && s.type !== 'mix-out');
  if (playable.length === 0) return [];

  const out: StructureGroup[] = [];
  for (const s of playable) {
    const last = out[out.length - 1];
    if (last && last.type === s.type) {
      last.count += 1;
      last.endTime = s.endTime;
    } else {
      out.push({ type: s.type, count: 1, startTime: s.startTime, endTime: s.endTime });
    }
  }
  return out;
}

const TYPE_LABEL: Record<SectionType, string> = {
  'mix-in':    'Mix In',
  'intro':     'Intro',
  'verse':     'Verse',
  'chorus':    'Chorus',
  'bridge':    'Bridge',
  'breakdown': 'Breakdown',
  'main-drop': 'Drop',
  'build':     'Build',
  'outro':     'Outro',
  'mix-out':   'Mix Out',
};

export function structureGroupLabel(g: StructureGroup): string {
  const base = TYPE_LABEL[g.type];
  return g.count > 1 ? `${g.count}× ${base}` : base;
}

/**
 * One-line vibe descriptor — a song-level read of energy + brightness +
 * loudness, designed to fit on a single line under the structure pills.
 */
export function describeVibe(analysis: AnalysisResult, sections: Section[]): string {
  const parts: string[] = [];

  // Energy arc — compare avg of first third vs last third
  if (sections.length >= 3) {
    const third = Math.max(1, Math.floor(sections.length / 3));
    const head = avg(sections.slice(0, third).map(s => s.avgEnergy));
    const tail = avg(sections.slice(-third).map(s => s.avgEnergy));
    const peak = Math.max(...sections.map(s => s.avgEnergy));
    if (peak > 0.7 && tail > head + 0.1) parts.push('builds and lands hard');
    else if (peak > 0.7) parts.push('peaks with a strong drop');
    else if (tail < head - 0.1) parts.push('starts strong, settles into a wind-down');
    else parts.push('steady energy throughout');
  }

  // Brightness from spectral centroid
  const cent = analysis.spectral?.centroid;
  if (cent) {
    if (cent > 3000) parts.push('bright / treble-tilted');
    else if (cent < 2000) parts.push('dark / bass-forward');
    else parts.push('balanced spectrum');
  }

  // Mastering tightness from dynamic range
  const dr = analysis.dynamicRange?.complexity;
  if (typeof dr === 'number') {
    if (dr < 6) parts.push('tightly mastered');
    else if (dr > 12) parts.push('wide dynamics');
  }

  return parts.join(' · ');
}

function avg(xs: number[]): number {
  if (xs.length === 0) return 0;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}
